import { labelError, normalizeLabel, rangeError } from "./notesForm.mjs";

/**
 * @typedef {object} AttributeDraft The edits of one row of the Attributes view.
 * @property {string} name
 * @property {number | null} min
 * @property {number | null} max
 */

/**
 * @typedef {object} AttributeRecord An attribute as the API returns it. Only the keys that this file reads.
 * @property {string} name
 * @property {number | null} min_value
 * @property {number | null} max_value
 * @property {number} usage_count
 * @property {{ uuid: string, name: string } | null} created_by
 */

export const ATTRIBUTES_DESCRIPTION =
  "An attribute can be deleted only when no note uses it. A new range must contain every saved value.";

const countNotes = (count) => `${count} ${count === 1 ? "note" : "notes"}`;

/**
 * The attributes sorted by name, ignoring case. The input list is not changed.
 * @template {{ name: string }} A
 * @param {A[]} attributes
 * @returns {A[]}
 */
export const sortAttributesByName = (attributes) =>
  [...attributes].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

/**
 * @param {AttributeRecord} attribute
 * @returns {AttributeDraft}
 */
export const draftOf = (attribute) => ({ name: attribute.name, min: attribute.min_value, max: attribute.max_value });

/**
 * The keys of the update request: only what the draft changes. An empty object means no change.
 * @param {AttributeRecord} attribute
 * @param {AttributeDraft} draft
 * @returns {{ name?: string, min_value?: number | null, max_value?: number | null }}
 */
export const attributePatch = (attribute, draft) => {
  const patch = {};
  const name = normalizeLabel(draft.name);
  if (name !== attribute.name) patch.name = name;
  if ((draft.min ?? null) !== attribute.min_value) patch.min_value = draft.min ?? null;
  if ((draft.max ?? null) !== attribute.max_value) patch.max_value = draft.max ?? null;
  return patch;
};

/**
 * The messages next to the inputs of a row, with the rules of the note dialog.
 * @param {AttributeDraft} draft
 * @returns {{ name: string | null, range: string | null }}
 */
export const attributeDraftErrors = (draft) => ({ name: labelError(draft.name), range: rangeError(draft.min, draft.max) });

/**
 * The input that shows an error of the server, from the `field` of the error.
 * The `range` error and the errors of the two ends show next to the range. Any other field has no input in a row.
 * @param {string | null} field
 * @returns {"name" | "range" | null}
 */
export const errorSlot = (field) => {
  if (field === "name") return "name";
  return field === "range" || field === "min_value" || field === "max_value" ? "range" : null;
};

/**
 * The question that the Save button asks when it renames an attribute that notes use. Null when it needs no question.
 * @param {AttributeRecord} attribute
 * @param {{ name?: string }} patch
 * @returns {string | null}
 */
export const renameConfirmText = (attribute, patch) =>
  patch.name === undefined || attribute.usage_count === 0
    ? null
    : `Rename “${attribute.name}” to “${patch.name}”? This changes the label on ${countNotes(attribute.usage_count)}.`;

/**
 * @param {number} usageCount
 * @returns {string}
 */
export const usageText = (usageCount) => (usageCount === 0 ? "Not used" : countNotes(usageCount));
